import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sparkle } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { MaskedLine } from "@/components/motion";
import { HeroVisual } from "@/components/HeroVisual";

const HERO_IMG = "/images/hero-now-booking.jpg";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 3]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center pt-36 pb-24 overflow-hidden"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-8 left-0 font-serif italic text-[24vw] leading-none text-stone-900/[0.045] select-none whitespace-nowrap"
      >
        forever
      </div>

      <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-14 items-center relative">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="inline-flex items-center gap-2 rounded-full border border-gold/25 bg-gold/10 px-4 py-1.5 mb-8"
            data-testid="hero-badge"
          >
            <Sparkle size={12} className="text-gold" />
            <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-gold-dark/80">
              Permanent Jewelry
            </span>
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="font-serif tracking-tight leading-[1.02] text-ink text-5xl sm:text-6xl lg:text-7xl"
          >
            <MaskedLine delay={0.15}>Faithfully Curated.</MaskedLine>
            <MaskedLine delay={0.32}>
              Beautifully <span className="italic text-gold">Linked.</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}
            data-testid="hero-subcopy"
            className="mt-8 max-w-md text-base leading-relaxed text-stone-600"
          >
            Custom-fitted bracelets and anklets, welded shut in a single spark. No clasp, no
            fuss — a piece you never take off, made for the moments you never forget.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-reserve-button"
              onClick={() => scrollToId("booking")}
              className="group rounded-full bg-gold text-white px-7 py-3.5 text-sm font-medium inline-flex items-center gap-2 hover:bg-gold-dark transition-colors duration-300 shadow-[0_10px_30px_rgba(26,56,140,0.35)]"
            >
              Reserve Your Curated Experience
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-explore-button"
              onClick={() => scrollToId("collection")}
              className="rounded-full border border-ink/15 px-7 py-3.5 text-sm font-medium text-ink hover:border-gold hover:text-gold transition-colors duration-300"
            >
              Explore Collection
            </button>
          </motion.div>
        </div>

        <HeroVisual y={y} rotate={rotate} image={HERO_IMG} />
      </div>
    </section>
  );
}
